import React, { Component, useState } from 'react';
import { Field } from 'react-final-form';
import moment from 'moment';
import classNames from 'classnames';

import SingleDatePicker from '../DatePicker/DatePickers/SingleDatePicker';
import DateRangePicker from '../DatePicker/DatePickers/DateRangePicker';
import { ValidationError } from '../../components';
import { generateDateRange } from '../../util/eventDateHelpers';
import css from './CustomExtendedDataField.module.css';

// Helper to extract label from fieldConfig (matches other custom field components)
const getLabel = fieldConfig => fieldConfig?.saveConfig?.label || fieldConfig?.label;

/**
 * Parse stored event dates (JSON array string) to array of timestamps
 * Handles backward compatibility with single timestamp format
 */
const parseEventDates = value => {
  if (!value) return [];
  
  // If it's a number (backward compat with single date), wrap in array
  if (typeof value === 'number') {
    return [value];
  }
  
  // If it's a JSON string array, parse it
  if (typeof value === 'string') {
    try {
      const parsed = JSON.parse(value);
      if (Array.isArray(parsed)) {
        return parsed.filter(v => typeof v === 'number').sort((a, b) => a - b);
      }
    } catch (e) {
      // If parsing fails, return empty array
      return [];
    }
  }
  
  // If it's already an array
  if (Array.isArray(value)) {
    return value.filter(v => typeof v === 'number').sort((a, b) => a - b);
  }
  
  return [];
};

/**
 * Convert array of timestamps to JSON string for storage
 */
const formatEventDates = dates => {
  if (!dates || dates.length === 0) {
    return null;
  }
  const uniqueDates = [...new Set(dates)].sort((a, b) => a - b);
  return JSON.stringify(uniqueDates);
};

/**
 * FieldEventDateComponent - Supports multiple event dates
 * Stores as JSON array of timestamps: "[1234567890, 2345678901]"
 */
const FieldEventDateComponent = props => {
  const { input, meta, id, fieldConfig, className, ...rest } = props;
  
  const label = getLabel(fieldConfig);
  const placeholderText = fieldConfig?.saveConfig?.placeholderMessage || 'Select date';
  
  // State for toggling range picker visibility
  const [showRangePicker, setShowRangePicker] = useState(false);
  const [rangeFeedback, setRangeFeedback] = useState('');
  
  // Parse stored value to array of timestamps
  const dates = parseEventDates(input.value);
  
  // Handle adding a new date
  const handleAddDate = selectedDate => {
    if (!selectedDate) return;
    
    const newTimestamp = moment(selectedDate).valueOf();
    const updatedDates = [...dates, newTimestamp];
    
    // Store as JSON string
    input.onChange(formatEventDates(updatedDates));
  };
  
  // Handle adding a date range
  const handleAddDateRange = dateRange => {
    if (!dateRange || dateRange.length !== 2) return;
    
    const [startDate, endDate] = dateRange;
    const newTimestamps = generateDateRange(startDate, endDate);
    
    if (newTimestamps.length === 0) return;
    
    // Merge with existing dates, remove duplicates
    const mergedDates = [...dates, ...newTimestamps];
    const uniqueDates = [...new Set(mergedDates)].sort((a, b) => a - b);
    
    // Calculate how many new dates were added (excluding duplicates)
    const newDatesCount = uniqueDates.length - dates.length;
    
    // Store as JSON string
    input.onChange(formatEventDates(uniqueDates));
    
    // Show feedback
    if (newDatesCount > 0) {
      const message = `${newDatesCount} date${newDatesCount === 1 ? '' : 's'} added from range`;
      setRangeFeedback(message);
      setTimeout(() => setRangeFeedback(''), 3000);
    } else {
      const message = 'All dates in range already added';
      setRangeFeedback(message);
      setTimeout(() => setRangeFeedback(''), 3000);
    }
  };
  
  // Handle removing a date at specific index
  const handleRemoveDate = indexToRemove => {
    const updatedDates = dates.filter((_, idx) => idx !== indexToRemove);
    input.onChange(formatEventDates(updatedDates));
  };

  // Allow all dates (past and future) for event listings
  const isOutsideRange = () => false;
  
  // Prevent blocked dates between range (not needed for events)
  const isBlockedBetween = () => false;

  return (
    <div className={classNames(css.customField, className)}>
      {label ? (
        <label className={css.customFieldLabel} htmlFor={id}>
          {label}
        </label>
      ) : null}
      
      {/* Display list of selected dates */}
      {dates.length > 0 && (
        <div className={css.eventDatesList}>
          {dates.map((timestamp, idx) => (
            <div key={timestamp} className={css.eventDateItem}>
              <span className={css.eventDateValue}>
                {moment(timestamp).format('MMM D, YYYY')}
              </span>
              <button
                type="button"
                className={css.eventDateRemoveBtn}
                onClick={() => handleRemoveDate(idx)}
                title="Remove this date"
              >
                ✕
              </button>
            </div>
          ))}
        </div>
      )}
      
      {/* Single date picker section */}
      <div className={css.eventDatePickerWrapper}>
        <label className={css.eventDatePickerLabel}>
          {dates.length > 0 ? 'Add another date' : 'Select first date'}
        </label>
        <SingleDatePicker
          id={id}
          value={null}
          onChange={handleAddDate}
          isOutsideRange={isOutsideRange}
          placeholderText={placeholderText}
          {...rest}
        />
      </div>
      
      {/* Date range picker section (accordion style) */}
      <div className={css.eventDateRangeSection}>
        <button
          type="button"
          className={css.eventDateRangeToggle}
          onClick={() => setShowRangePicker(!showRangePicker)}
        >
          <span className={css.eventDateRangeToggleIcon}>
            {showRangePicker ? '▼' : '▶'}
          </span>
          <span className={css.eventDateRangeToggleText}>
            Add Date Range
          </span>
        </button>
        
        {showRangePicker && (
          <div className={css.eventDateRangeContent}>
            <p className={css.eventDateRangeDescription}>
              Select a start and end date to add all dates in between
            </p>
            <DateRangePicker
              startDateId={`${id}-range-start`}
              endDateId={`${id}-range-end`}
              startDatePlaceholderText="Start date"
              endDatePlaceholderText="End date"
              value={null}
              onChange={handleAddDateRange}
              isOutsideRange={isOutsideRange}
              isBlockedBetween={isBlockedBetween}
            />
            {rangeFeedback && (
              <div className={css.eventDateRangeFeedback}>
                {rangeFeedback}
              </div>
            )}
          </div>
        )}
      </div>
      
      <ValidationError fieldMeta={meta} />
    </div>
  );
};

/**
 * FieldEventDate - Wrapper that integrates with Final Form for multiple event date selection
 * 
 * This field stores dates as a JSON array of Unix timestamps in the backend,
 * but presents an easy-to-use date picker UI with add/remove functionality.
 * 
 * Backward compatible: single timestamp values are converted to array format on first save.
 * 
 * @component
 * @param {Object} props - Component props
 * @param {string} props.name - Field name (e.g., 'publicData.eventDate')
 * @param {string} props.fieldConfig - Field configuration including saveConfig.label and saveConfig.placeholderMessage
 * @param {string} props.id - Field ID
 * @param {Function} props.validate - Validation function
 * @returns {JSX.Element}
 */
class FieldEventDate extends Component {
  render() {
    return <Field component={FieldEventDateComponent} {...this.props} />;
  }
}

export default FieldEventDate;
